import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Container, Row, Col, Alert } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
// import WeatherWidget from './WeatherWidget'
import { useContent } from '../hooks/useContent'
import { ContentService } from '../services/contentService'
import storyImage from '../assets/story.jpg'
import blog1 from '../assets/blog1.jpg'
import blog2 from '../assets/blog2.jpg'
import blog3 from '../assets/blog3.jpg'
import blog4 from '../assets/blog4.jpg'
import gallery1 from '../assets/gal1.jpg'
import stay1 from '../assets/stay1.png'
import stay2 from '../assets/stay2.png'
import stay3 from '../assets/stay3.png'
import stay4 from '../assets/stay4.png'
import cardBg1 from '../assets/exp1.jpg'
import googleLogo from '../assets/google.png'
import tripadvisorLogo from '../assets/tripadv.png'
// removed unused icon import to reduce bundle size
import StackedGallery from './StackedGallery.js'
import FloatingActionButton from './FloatingActionButton'
import { mediaPrefetcher } from '../utils/mediaPrefetcher'
import { registerServiceWorker } from '../utils/serviceWorkerRegistration'
import './Home.css'
import Footer from './footer.js'





const decodeImageAsset = (url) => {
  if (!url || typeof url !== 'string') return Promise.resolve()
  if (typeof Image === 'undefined') return Promise.resolve()

  return new Promise((resolve, reject) => {
    try {
      const img = new Image()

      const cleanup = () => {
        img.onload = null
        img.onerror = null
      }

      img.onload = () => {
        if (typeof img.decode === 'function') {
          img
            .decode()
            .then(() => {
              cleanup()
              resolve()
            })
            .catch(() => {
              cleanup()
              resolve()
            })
        } else {
          cleanup()
          resolve()
        }
      }

      img.onerror = () => {
        cleanup()
        reject(new Error('Image failed to load'))
      }

      img.src = url
    } catch (error) {
      resolve()
    }
  })
}

const bufferVideoAsset = (url) => {
  if (!url || typeof url !== 'string') return Promise.resolve()
  if (typeof document === 'undefined') return Promise.resolve()

  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video')
      video.preload = 'auto'
      video.muted = true
      video.playsInline = true

      const cleanup = () => {
        video.removeEventListener('loadeddata', onReady)
        video.removeEventListener('canplay', onReady)
        video.removeEventListener('canplaythrough', onReady)
        video.removeEventListener('error', onError)
        video.src = ''
      }

      const settle = (result) => {
        cleanup()
        result ? resolve() : reject(new Error('Video failed to buffer'))
      }

      const onReady = () => settle(true)
      const onError = () => settle(false)

      video.addEventListener('loadeddata', onReady)
      video.addEventListener('canplay', onReady)
      video.addEventListener('canplaythrough', onReady)
      video.addEventListener('error', onError)

      video.src = url
      video.load()

      setTimeout(() => settle(true), 10000)
    } catch (error) {
      resolve()
    }
  })
}

const scheduleIdleTask = (cb, timeout = 1200) => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    return window.requestIdleCallback(cb, { timeout })
  }
  return setTimeout(cb, timeout)
}

const Home = () => {
  const [initialHeroLoaded, setInitialHeroLoaded] = useState(false)
  const assetPromisesRef = useRef(new Map())
  const lazyQueueRef = useRef([])
  const lazyWorkerActiveRef = useRef(0)
  const lazyInitializedRef = useRef(false)
  const heroIndexRef = useRef(0)

  // Dynamic content hook (loads after video URL is fetched)
  const {
    content,
    loading: contentLoading,
    error: contentError,
  } = useContent('home')

  const navigate = useNavigate()

  const [discoverIndex, setDiscoverIndex] = useState(0)
  // Loader removed: render hero media immediately

  // Use content data if available, otherwise fallback to defaults
  const heroContent = content?.hero || {
    slides: [
      {
        heading: 'Welcome to Ampthilldowns',
        subheading:
          'An eco-stay in the mountains where nature and comfort come together',
      },
      {
        heading: 'Imagine spending your day',
        subheading: 'listening to birds chirping, the wind whistling past',
      },
      {
        heading: 'Creating Memorable Experiences',
        subheading: 'Adventures, relaxation and wellness',
      },
      {
        heading: "We can't wait to take care of you",
        subheading: 'Live the Mountain Experience. Be our Guest',
      },
    ],
    ctaText: 'Book Your Stay',
  }

  // debug removed

  // Total number of text slides to show in the hero
  const slidesCount = (heroContent?.slides && heroContent.slides.length) || 0

  const introContent = content?.intro || {
    title: 'Ampthilldowns',
    content:
      "A destination stay where you reconnect with nature, with each other, and with themselves. More than just a getaway it's a place where loved ones gather, where animals roam free, where time slows down. A sanctuary for people, for nature; a place where everything and everyone finds their rhythm.",
  }

  const locationContent = content?.location || {
    title: 'Ampthilldowns',
    coordinates: {
      latitude: '10°11\'33.76"N',
      longitude: '77°20\'17.40"E',
      elevation: '6,515 ft',
    },
    paragraphs: [
      'Welcome to Ampthilldowns, a secluded haven nestled in the untouched village of Poondi, perched on the borders of Tamil Nadu and Kerala.',
      'The resort spans over 40 acres of rolling hills, wetlands, ponds, grasslands, green meadows and preserved native flora.',
      'Ampthilldowns lies directly on an ancient wild animal migration route, surrounded by lush forests and crystal-clear lakes.',
      'Ampthilldowns is more than just a retreat; it is a destination for discovery, for reconnecting with oneself and the natural world.',
      'Whether you come to explore rare flora and fauna, explore ancient landscapes, or simply unwind in pristine surroundings, Ampthilldowns promises an experience rooted in sustainability, beauty, and tranquillity.',
      'Welcome to your sanctuary in the hills.',
    ],
  }

  // Use content from Firebase with proper fallback structure
  const storyContent = content?.story || {}
  const accommodationContent = content?.accommodation || { types: [] }
  const experiencesContent = content?.experiences || { sections: [] }
  const sustainableLivingContent = content?.sustainableLiving || {
    sections: [],
  }
  const testimonialsContent = content?.testimonials || { reviews: [] }

  const [heroImage, setHeroImage] = useState('')
  const [heroVideo, setHeroVideo] = useState('')
  const [videoReady, setVideoReady] = useState(false) // Track when full quality video is ready
  const [heroImageLoaded, setHeroImageLoaded] = useState(false)
  const [isInitialMedia, setIsInitialMedia] = useState(true) // Track if showing initial placeholder media
  const [firstSlideReady, setFirstSlideReady] = useState(false)
  // REMOVED: Pending media state - videos/images now load directly
  // Video element ref for direct control and immediate playback
  const videoRef = useRef(null)
  // Prefetch guard
  const prefetchStartedRef = useRef(false)
  // Ready caches so we only swap media when the next slide is ready
  const readyImagesRef = useRef(new Set())
  const readyVideosRef = useRef(new Set())
  const LAZY_MAX_CONCURRENT = 1

  const ensureAssetReady = useCallback((url, type) => {
    if (!url) return Promise.resolve()
    const readySet =
      type === 'image' ? readyImagesRef.current : readyVideosRef.current
    if (readySet.has(url)) return Promise.resolve()

    const key = `${type}:${url}`
    if (assetPromisesRef.current.has(key)) {
      return assetPromisesRef.current.get(key)
    }

    const loader =
      type === 'image' ? decodeImageAsset(url) : bufferVideoAsset(url)

    const promise = loader
      .catch(() => {})
      .then(() => {
        readySet.add(url)
      })
      .finally(() => {
        assetPromisesRef.current.delete(key)
      })

    assetPromisesRef.current.set(key, promise)
    return promise
  }, [])

  const loadSlideMedia = useCallback(
    async (slide, options = {}) => {
      if (!slide) return
      const { imageFirst = true } = options
      const hasImage = Boolean(slide.backgroundImage)
      const hasVideo = Boolean(slide.backgroundVideo)

      if (hasImage && imageFirst) {
        await ensureAssetReady(slide.backgroundImage, 'image')
      }

      if (hasVideo) {
        await ensureAssetReady(slide.backgroundVideo, 'video')
      }

      if (hasImage && !imageFirst) {
        await ensureAssetReady(slide.backgroundImage, 'image')
      }
    },
    [ensureAssetReady]
  )

  const processLazyQueue = useCallback(() => {
    if (lazyWorkerActiveRef.current >= LAZY_MAX_CONCURRENT) return
    const nextSlide = lazyQueueRef.current.shift()
    if (!nextSlide) return

    const start = () => {
      lazyWorkerActiveRef.current += 1
      loadSlideMedia(nextSlide)
        .catch(() => {})
        .finally(() => {
          lazyWorkerActiveRef.current -= 1
          processLazyQueue()
        })
    }

    scheduleIdleTask(start, 1000)
  }, [LAZY_MAX_CONCURRENT, loadSlideMedia])

  const enqueueSlidesForLazyLoad = useCallback(
    (slides) => {
      if (!slides || slides.length === 0) return
      lazyQueueRef.current.push(...slides)
      processLazyQueue()
    },
    [processLazyQueue]
  )

  useEffect(() => {
    let cancelled = false

    const hydrateFirstSlide = async () => {
      try {
        const [firstSlide] = await ContentService.getHeroSlides({ limit: 1 })
        if (cancelled) return

        if (!firstSlide) {
          setFirstSlideReady(true)
          setInitialHeroLoaded(true)
          return
        }

        // Immediately show first media without waiting for decode/buffer
        if (firstSlide.backgroundImage) {
          setHeroImage(firstSlide.backgroundImage)
          // Warm the cache in background (non-blocking)
          ensureAssetReady(firstSlide.backgroundImage, 'image').catch(() => {})
        } else if (firstSlide.backgroundVideo) {
          ensureAssetReady(firstSlide.backgroundVideo, 'video').catch(() => {})
        }

        if (cancelled) return

        heroIndexRef.current = 0
        setCurrentHeroIndex(0)
        setIsInitialMedia(false)

        if (firstSlide.backgroundVideo) {
          setHeroVideo(firstSlide.backgroundVideo)
          setVideoReady(false)
        } else {
          setHeroVideo('')
        }

        setInitialHeroLoaded(true)
        setFirstSlideReady(true)
      } catch (error) {
        if (!cancelled) {
          setFirstSlideReady(true)
          setInitialHeroLoaded(true)
        }
      }
    }

    hydrateFirstSlide()

    return () => {
      cancelled = true
    }
  }, [ensureAssetReady])

  useEffect(() => {
    if (!firstSlideReady) return
    if (lazyInitializedRef.current) return

    let cancelled = false

    const loadRemainingSlides = async () => {
      try {
        const slides = await ContentService.getHeroSlides()
        if (cancelled) return
        if (!slides?.length) {
          lazyInitializedRef.current = true
          return
        }
        const remaining = slides.slice(1)
        if (remaining.length === 0) {
          lazyInitializedRef.current = true
          return
        }
        lazyInitializedRef.current = true
        enqueueSlidesForLazyLoad(remaining)
      } catch (error) {
        // Silent fail; hero will continue with available slides
      }
    }

    loadRemainingSlides()

    return () => {
      cancelled = true
    }
  }, [firstSlideReady, enqueueSlidesForLazyLoad])

  // New state for carousels
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0)

  useEffect(() => {
    heroIndexRef.current = currentHeroIndex
  }, [currentHeroIndex])

  // Prefetch helpers (defined before use) - Enhanced with multi-strategy prefetching
  const prefetchImage = (url) => {
    return new Promise((resolve) => {
      if (!url || typeof url !== 'string') return resolve()
      try {
        // Use mediaPrefetcher for enhanced prefetching with multiple strategies
        mediaPrefetcher
          .prefetchImage(url, 'high')
          .then(() => {
            resolve()
          })
          .catch(() => {
            // Fallback to original method if prefetcher fails
            try {
              const img = new Image()
              const done = () => resolve()
              img.onload = () => {
                if (img.decode) {
                  img.decode().then(done).catch(done)
                } else {
                  done()
                }
              }
              img.onerror = done
              img.src = url
            } catch (e) {
              resolve()
            }
          })
      } catch (e) {
        resolve()
      }
    })
  }


  useEffect(() => {
  const items = document.querySelectorAll(".ta-facts .fact");
  let i = 0;

  const cycle = () => {
    items.forEach(item => item.classList.remove("active"));
    items[i].classList.add("active");
    i = (i + 1) % items.length;
  };

  cycle();
  const interval = setInterval(cycle, 3500);

  return () => clearInterval(interval);
}, []);













  const prefetchVideo = (url) => {
    return new Promise((resolve) => {
      if (!url || typeof url !== 'string') return resolve()
      try {
        // Use mediaPrefetcher for enhanced prefetching with multiple strategies
        mediaPrefetcher
          .prefetchVideo(url, 'high')
          .then(() => {
            resolve()
          })
          .catch(() => {
            // Fallback to original method if prefetcher fails
            try {
              const v = document.createElement('video')
              let settled = false
              const done = () => {
                if (settled) return
                settled = true
                cleanup()
                resolve()
              }
              const onReady = () => done()
              const onError = () => done()
              const cleanup = () => {
                v.removeEventListener('loadeddata', onReady)
                v.removeEventListener('canplay', onReady)
                v.removeEventListener('canplaythrough', onReady)
                v.removeEventListener('error', onError)
              }
              v.preload = 'auto'
              v.muted = true
              v.addEventListener('loadeddata', onReady)
              v.addEventListener('canplay', onReady)
              v.addEventListener('canplaythrough', onReady)
              v.addEventListener('error', onError)
              v.src = url
              v.load()
              // Safety timeout: don't wait forever
              setTimeout(done, 2000)
            } catch (e) {
              resolve()
            }
          })
      } catch (e) {
        resolve()
      }
    })
  }

  // Ensure helpers that resolve when media is ready
  // REMOVED: Prefetch dependency - videos load directly in video element
  // This was causing blank pages when prefetch failed
  const ensureVideoReady = (url) => {
    return new Promise((resolve) => {
      if (!url) return resolve(false)
      // Don't wait for prefetch - videos load directly in video element
      // Prefetch is just an optimization, not a requirement
      resolve(true)
    })
  }

  // Compute initial slide: prefer first with video, else first with image, else 0
  const initialHeroIndex = useMemo(() => {
    const slides = heroContent?.slides || []
    const firstVideo = slides.findIndex((s) => s?.backgroundVideo)
    if (firstVideo >= 0) return firstVideo
    const firstImage = slides.findIndex((s) => s?.backgroundImage)
    if (firstImage >= 0) return firstImage
    return 0
  }, [heroContent])

  // No poster or fallback image used before video per request

  // Switch hero media - DIRECT LOADING (no prefetch dependency)
  // Videos and images load directly in their elements without waiting for prefetch
  // This prevents blank pages when prefetch fails
  const switchHeroToIndex = (index) => {
    if (!heroContent?.slides?.[index]) return;

    const slide = heroContent.slides[index]
    heroIndexRef.current = index
    setCurrentHeroIndex(index)
    setIsInitialMedia(false)

      // HIDE TEXT UNTIL IMAGE/VIDEO IS READY
    setIsHeroReady(false);

    const { backgroundImage, backgroundVideo } = slide

    // Reset loaded flags when switching
    setHeroImageLoaded(false)

    if (backgroundImage) {
      // Set image URL; we will update the displayed index when onLoad fires
      setHeroImage(backgroundImage)
      ensureAssetReady(backgroundImage, 'image').catch(() => {})
    } else {
      setHeroImage('')
    }

    if (backgroundVideo) {
      // Set video URL; displayed index will update when video starts playing
      setHeroVideo(backgroundVideo)
      setVideoReady(false)
      ensureAssetReady(backgroundVideo, 'video').catch(() => {})
    } else {
      setHeroVideo('')
    }
  }

  // AUTO ADVANCE IMAGE SLIDES (5 seconds)
useEffect(() => {
  const slides = heroContent?.slides || []
  if (!slides.length) return
  if (isInitialMedia) return

  const currentSlide = slides[currentHeroIndex]

  // If it's a video, do not auto-advance here
  if (currentSlide?.backgroundVideo) return

  // Auto advance image every 5 seconds
  const timer = setTimeout(() => {
    const next = (currentHeroIndex + 1) % slides.length
    switchHeroToIndex(next)
    setCurrentHeroIndex(next)
  }, 5000)

  return () => clearTimeout(timer)
}, [currentHeroIndex, heroContent, isInitialMedia])

const [isHeroReady, setIsHeroReady] = useState(false);

  // REMOVED: Pending media logic - videos/images now load directly
  // This was causing blank pages when prefetch failed

  // STEP 1: Show FIRST AVAILABLE media immediately (from fallback or Firebase)
  // This gives instant visual feedback while slider's first media loads
  useEffect(() => {
    // Skip if we already have media displayed
    if (heroImage || heroVideo) return
    

    // Get first available media from ANY source (fallback or Firebase)
    const slides = heroContent?.slides || []
    
    if (slides.length === 0) return

    const firstSlide = slides[0]

    // Show FIRST available media immediately (image or video)
    if (firstSlide?.backgroundImage) {
      // Show image immediately
      setHeroImage(firstSlide.backgroundImage)
      setHeroVideo('')
      setCurrentHeroIndex(0)
      setIsInitialMedia(true) // Mark as initial placeholder
      setVideoReady(false)
    } else if (firstSlide?.backgroundVideo) {
      // Show video immediately
      setHeroVideo(firstSlide.backgroundVideo)
      setHeroImage('')
      setCurrentHeroIndex(0)
      setIsInitialMedia(true) // Mark as initial placeholder
      setVideoReady(false)

      // Progressive buffering - set src and start loading
      if (videoRef.current) {
        videoRef.current.src = firstSlide.backgroundVideo
        videoRef.current.load() // Start loading (progressive buffering)
        // Browser will start playing when enough data is buffered
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroContent])

  // STEP 2: When slider's first media is ready, replace initial media
  // This ensures we show the actual configured slider media
  useEffect(() => {
    // Only replace if we're showing initial media
    if (!isInitialMedia) return

    const slides = heroContent?.slides || []
    if (slides.length === 0) return

    const firstSlide = slides[0]
    const currentMedia = heroVideo || heroImage

    // Check if slider's first media is different from what's showing
    if (
      firstSlide?.backgroundVideo &&
      currentMedia !== firstSlide.backgroundVideo
    ) {
      // Slider has video - replace with it
      if (firstSlide?.backgroundImage) {
        // Show image placeholder first
        setHeroImage(firstSlide.backgroundImage)
      }
      setHeroVideo(firstSlide.backgroundVideo)
      setIsInitialMedia(false) // No longer initial media
      setVideoReady(false)

      // Progressive buffering - set src and start loading
      if (videoRef.current) {
        videoRef.current.src = firstSlide.backgroundVideo
        videoRef.current.load() // Start loading (progressive buffering)
        // Browser will start playing when enough data is buffered
      }
    } else if (
      firstSlide?.backgroundImage &&
      currentMedia !== firstSlide.backgroundImage
    ) {
      // Slider has image - replace with it
      setHeroImage(firstSlide.backgroundImage)
      setHeroVideo('')
      setIsInitialMedia(false) // No longer initial media
      setVideoReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroContent, isInitialMedia, heroVideo, heroImage])

  // PROGRESSIVE BUFFERING: Start playing as FAST as possible
  useEffect(() => {
    if (!heroVideo || !videoRef.current) {
      setVideoReady(false)
      return
    }

    // Reset video ready state when video URL changes
    setVideoReady(false)

    // Set src and trigger loading IMMEDIATELY
    if (videoRef.current.src !== heroVideo) {
      videoRef.current.src = heroVideo
      videoRef.current.load() // Start loading immediately
    }

    // Try to play as soon as we have ANY data (readyState >= 1 for fastest playback)
    const tryPlay = () => {
      if (videoRef.current) {
        const video = videoRef.current
        // Attempt playback; don't mark ready until actual playing event
        if (video.readyState >= 1) {
          video.play().catch(() => {})
        }
      }
    }

    // Listen for events - try to play as early as possible
    videoRef.current.addEventListener('loadedmetadata', tryPlay, { once: true })
    videoRef.current.addEventListener('loadeddata', tryPlay, { once: true })
    videoRef.current.addEventListener('canplay', tryPlay, { once: true })

    // Try immediately and with short delays for fastest playback
    tryPlay()
    setTimeout(tryPlay, 50)
    setTimeout(tryPlay, 150)
  }, [heroVideo, heroImage])

  // Which slide index is actually displayed to users (used for overlay text).
  // We update this only when the media is visible (image onLoad or video playing).
  const [displayedSlideIndex, setDisplayedSlideIndex] = useState(0)


  // REMOVED: Full video preload - using progressive buffering instead (YouTube-style)
  // The video element with preload='metadata' will handle progressive loading

  // Safe derived values to avoid undefined access during initial render
  const storyParas = Array.isArray(storyContent?.paragraphs)
    ? storyContent.paragraphs
    : []
  const locationParas = Array.isArray(locationContent?.paragraphs)
    ? locationContent.paragraphs
    : []
  const locCoords = locationContent?.coordinates || {}

  // Prefetch helpers end

  // Register service worker on component mount
  useEffect(() => {
    try {
      registerServiceWorker().catch(() => {
        // Silent fail - app continues normally
      })
    } catch (e) {
      // Silent fail - app continues normally
    }
  }, [])

  // Start prefetching visible assets once content is available - Enhanced with priority-based prefetching
  useEffect(() => {
    if (contentLoading || !content) return
    if (prefetchStartedRef.current) return
    prefetchStartedRef.current = true

    // Collect asset URLs (key images/videos, blog)
    const imageUrls = []
    const videoUrls = []

    // welcome/story backgrounds
    if (locationContent?.backgroundImage)
      imageUrls.push(locationContent.backgroundImage)
    if (storyContent?.backgroundImage)
      imageUrls.push(storyContent.backgroundImage)

      // experiences cards (first 3)
    ;(experiencesContent?.sections || [])
      .slice(0, 3)
      .forEach((sec) => sec?.image && imageUrls.push(sec.image))

    // sustainable living (first 3)
    ;(sustainableLivingContent?.sections || [])
      .slice(0, 3)
      .forEach((sec) => sec?.image && imageUrls.push(sec.image))

    // blog images (if provided via content)
    ;(content?.discover?.posts || [])
      .slice(0, 4)
      .forEach((post) => post?.image && imageUrls.push(post.image))

    // Priority-based prefetching
    // High priority: First 8 images + first 3 videos (above-fold content)
    const highPriorityImages = imageUrls.slice(0, 8)
    const highPriorityVideos = videoUrls.slice(0, 3)

    // Low priority: Remaining content (below-fold)
    const lowPriorityImages = imageUrls.slice(8)
    const lowPriorityVideos = videoUrls.slice(3)

    // Prefetch high priority immediately (parallel, max 3 concurrent)
    if (highPriorityImages.length > 0) {
      mediaPrefetcher
        .prefetchBatch(highPriorityImages, 'image', 'high')
        .then((results) => {
          results.forEach((result, idx) => {
            if (result.status === 'fulfilled' && result.value) {
              readyImagesRef.current.add(highPriorityImages[idx])
            }
          })
        })
        .catch(() => {
          // Silent fail - continue with other prefetching
        })
    }

    if (highPriorityVideos.length > 0) {
      mediaPrefetcher
        .prefetchBatch(highPriorityVideos, 'video', 'high')
        .then((results) => {
          results.forEach((result, idx) => {
            if (result.status === 'fulfilled' && result.value) {
              readyVideosRef.current.add(highPriorityVideos[idx])
            }
          })
        })
        .catch(() => {
          // Silent fail - continue with other prefetching
        })
    }

    // Prefetch low priority after delay (non-blocking, deferred 2s)
    setTimeout(() => {
      if (lowPriorityImages.length > 0) {
        mediaPrefetcher
          .prefetchBatch(lowPriorityImages, 'image', 'low')
          .then((results) => {
            results.forEach((result, idx) => {
              if (result.status === 'fulfilled' && result.value) {
                readyImagesRef.current.add(lowPriorityImages[idx])
              }
            })
          })
          .catch(() => {
            // Silent fail
          })
      }

      if (lowPriorityVideos.length > 0) {
        mediaPrefetcher
          .prefetchBatch(lowPriorityVideos, 'video', 'low')
          .then((results) => {
            results.forEach((result, idx) => {
              if (result.status === 'fulfilled' && result.value) {
                readyVideosRef.current.add(lowPriorityVideos[idx])
              }
            })
          })
          .catch(() => {
            // Silent fail
          })
      }
    }, 2000) // Defer low priority by 2 seconds

    // Also maintain original prefetch tasks for backward compatibility
    const imageTasks = imageUrls.map((u) =>
      prefetchImage(u).then(() => readyImagesRef.current.add(u))
    )
    const videoTasks = videoUrls.map((u) =>
      prefetchVideo(u).then(() => readyVideosRef.current.add(u))
    )
    // Fire and forget; we don't block on completion beyond the splash duration
    Promise.allSettled([...imageTasks, ...videoTasks]).catch(() => {})
  }, [
    contentLoading,
    content,
    heroContent,
    locationContent,
    storyContent,
    experiencesContent,
    sustainableLivingContent,
  ])

  // Loader logic removed

  // Defensive DOM sanitization: some legacy code or external data may still
  // reference storage paths under `images/rooms/...` which can produce
  // repeated 404s in the browser console if the objects don't exist.
  // To avoid noisy errors on the Home page we proactively replace such
  // references with a local fallback image and install an error handler
  // + MutationObserver to catch future dynamic additions.
  useEffect(() => {
    const FALLBACK = gallery1 // local asset imported above

    const looksLikeRoomPath = (url) => {
      if (!url) return false
      try {
        return (
          url.includes('/images/rooms/') || url.includes('images%2Frooms%2F')
        )
      } catch (e) {
        return false
      }
    }

    const sanitizeElement = (el) => {
      try {
        if (el instanceof HTMLImageElement) {
          if (looksLikeRoomPath(el.src)) {
            el.src = FALLBACK
            el.dataset.replaced = 'true'
          }
        } else if (el instanceof HTMLElement) {
          const bg = window
            .getComputedStyle(el)
            .getPropertyValue('background-image')
          if (bg && looksLikeRoomPath(bg)) {
            el.style.backgroundImage = `url(${FALLBACK})`
            el.dataset.replaced = 'true'
          }
        }
      } catch (e) {
        // silent
      }
    }

    const sanitizeAll = () => {
      document.querySelectorAll('img').forEach(sanitizeElement)
      document.querySelectorAll('[style]').forEach((el) => sanitizeElement(el))
    }

    // run once on mount
    sanitizeAll()

    // error handler for images that fail to load
    const onImgError = (evt) => {
      const t = evt.target
      if (t && t instanceof HTMLImageElement) {
        if (looksLikeRoomPath(t.src) || t.dataset.replaced === 'true') {
          t.src = FALLBACK
        }
      }
    }

    window.addEventListener('error', onImgError, true)

    // observe DOM to sanitize newly added nodes
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList') {
          m.addedNodes.forEach((n) => {
            if (n instanceof HTMLElement) {
              sanitizeElement(n)
              n.querySelectorAll &&
                n.querySelectorAll('img, [style]').forEach(sanitizeElement)
            }
          })
        } else if (m.type === 'attributes' && m.target instanceof HTMLElement) {
          sanitizeElement(m.target)
        }
      }
    })

    mo.observe(document.documentElement || document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'style'],
    })

    return () => {
      window.removeEventListener('error', onImgError, true)
      try {
        mo.disconnect()
      } catch (e) {}
    }
  }, [])

  // Auto-advance hero carousel with timing
  // Images: Display for 7 seconds, then advance
  // Videos: Play full length, then advance via onEnded handler
  useEffect(() => {
    if (slidesCount <= 1) return
    const currentSlideHasVideo =
      heroContent?.slides?.[currentHeroIndex]?.backgroundVideo
    // Don't auto-advance if slide contains a video - let video finish first
    if (currentSlideHasVideo) return
    // Don't auto-advance if we're showing initial placeholder
    if (isInitialMedia) return

    // Image-only slides: Display for 7 seconds, then advance
    const interval = setInterval(() => {
      setCurrentHeroIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % slidesCount
    
        // const nextSlide = heroContent?.slides?.[nextIndex]

        // Advance to next slide (whether image or video)
        // Videos will handle their own timing via onEnded
        // Images will display for 7 seconds then advance
        switchHeroToIndex(nextIndex)
        return prevIndex
      })
    }, 7000) // Change slide every 7 seconds for images

    return () => clearInterval(interval)
  }, [slidesCount, heroContent, currentHeroIndex, isInitialMedia])

  // Backwards-compat wrapper removed (unused)

  const handleNextDiscover = () => {
    setDiscoverIndex((prevIndex) => {
      const sectionsLength = experiencesContent?.sections?.length || 1
      const newIndex = (prevIndex + 1) % sectionsLength
      return newIndex === prevIndex ? (newIndex + 1) % sectionsLength : newIndex
    })
  }

  const handlePrevDiscover = () => {
    setDiscoverIndex((prevIndex) => {
      const sectionsLength = experiencesContent?.sections?.length || 1
      const newIndex = (prevIndex - 1 + sectionsLength) % sectionsLength
      return newIndex === prevIndex
        ? (newIndex - 1 + sectionsLength) % sectionsLength
        : newIndex
    })
  }

  // Removed auto-scroll timer for Discover Experiences section per request.
  // Manual navigation via handleNextDiscover / handlePrevDiscover retained.

  // Removed auto-scroll timer for Sustainable Living section per request.
  // Manual navigation via handleNextSustainable / handlePrevSustainable retained.

  // Section ID mappings for navigation
  const experienceSectionIds = [
    'on-property-hikes',
    'farm-and-garden',
    'fireside-conversations',
    'nature-trails',
  ]

  const sustainableSectionIds = [
    'shola-grassland',
    'grassland-wetland',
    'rainwater-harvesting',
    'biogas-generation',
    'off-grid-power',
    'zero-waste',
  ]

  // Normalize and validate experience sectionId before using it
  const normalizeExperienceSectionId = (sectionId, index) => {
    const ids = experienceSectionIds || []
    const len = ids.length || 1
    if (typeof sectionId === 'string') {
      const normalized = sectionId.trim().toLowerCase()
      if (ids.includes(normalized)) return normalized
    }
    return ids[index % len]
  }

  // Navigation for experience cards with specific section targeting
  const handleExperienceCardClick = (sectionId, fallbackIndex) => {
    const finalSectionId = normalizeExperienceSectionId(
      sectionId,
      fallbackIndex
    )
    if (!finalSectionId) return
    navigate(`/experiences?section=${finalSectionId}`)
  }

  const handleFarmCardClick = (sectionId, fallbackIndex) => {
    const finalSectionId =
      sectionId ||
      sustainableSectionIds[
        fallbackIndex % (sustainableSectionIds?.length || 1)
      ]
    navigate(`/the-farm?section=${finalSectionId}`)
  }
  // Removed unused farm scroll helpers

  // Independent index for Sustainable Living carousel (so it doesn't share discoverIndex)
  const [sustainableIndex, setSustainableIndex] = useState(0)

  const handleNextSustainable = () => {
    setSustainableIndex((prevIndex) => {
      const len = sustainableLivingContent?.sections?.length || 1
      return (prevIndex + 1) % len
    })
  }

  const handlePrevSustainable = () => {
    setSustainableIndex((prevIndex) => {
      const len = sustainableLivingContent?.sections?.length || 1
      return (prevIndex - 1 + len) % len
    })
  }

  // Detect mobile/tablet viewport (matches CSS breakpoint <=1024px) for scrollable full list
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const updateIsMobile = () => {
      try {
        setIsMobile(window.innerWidth <= 1024)
      } catch (e) {
        setIsMobile(false)
      }
    }
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    return () => window.removeEventListener('resize', updateIsMobile)
  }, [])

  // TripAdvisor widget removed
  const handleNextHero = () => {
    setCurrentHeroIndex((prevIndex) => {
      const base = slidesCount > 0 ? slidesCount : 1
      const nextIndex = (prevIndex + 1) % base
      switchHeroToIndex(nextIndex)
      return prevIndex
    })
  }

  const handlePrevHero = () => {
    setCurrentHeroIndex((prevIndex) => {
      const base = slidesCount > 0 ? slidesCount : 1
      const target = (prevIndex - 1 + base) % base
      switchHeroToIndex(target)
      return prevIndex
    })
  }

  const [expanded, setExpanded] = useState(false)

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }
  // For Story section expand/collapse
  const [storyExpanded, setStoryExpanded] = useState(false)

  const toggleStoryExpanded = () => {
    setStoryExpanded(!storyExpanded)
  }

  // Show error state if content failed to load (only after loading completes)
  if (!contentLoading && (contentError || !content)) {
    return (
      <div className='home-page'>
        <Alert variant='danger' className='text-center mb-0'>
          <strong>Content Loading Error:</strong>{' '}
          {contentError || 'Failed to load content'}
          <p className='mt-2'>Please try refreshing the page.</p>
        </Alert>
      </div>
    )
  }

  return (
    <div className='home-page'>
      {/* Loading screen - shows until video is ready to play */}
      {/* Loader removed for instant hero paint */}
      {/* splash loader intentionally removed (except first visit) */}
      {/* image error alert removed; fallback sanitation handles failures silently */}

      {/* <WeatherWidget /> */}

      {/* Hero Section */}
      <section className='hero-section hero-carousel-container'>
        {/* Removed hero left/right arrow buttons per request; auto-advance logic retained */}
        <div className='hero-media-layer'>
          {/* Active media - ALWAYS RENDER for fastest loading */}
          {/* Render video element immediately, even if URL not set yet */}
          <video
            ref={videoRef}
            className='hero-video active-media'
            key={`active-v-${heroVideo || 'loading'}`}
            autoPlay
            muted
            playsInline
            preload='auto'
            fetchPriority='high'
            poster={heroImage || (heroContent?.slides?.[currentHeroIndex]?.backgroundImage || '')}
            style={{
              // Hide the video element entirely until it actually starts playing
              display: videoReady ? 'block' : 'none',
              opacity: videoReady ? 1 : 0,
            }}
            onLoadedMetadata={(e) => {
              const video = e.target
              if (video) {
                // Attempt playback; keep image until we are actually playing
                video.play().catch(() => {})
              }
            }}
            onLoadedData={(e) => {
              const video = e.target
              if (video && video.paused) {
                video.play().catch(() => {})
              }
            }}
            onCanPlay={(e) => {
              const video = e.target
              if (video && video.paused) {
                video.play().catch(() => {})
              }
            }}
            onProgress={(e) => {
              // Video is buffering - try to play as soon as we have ANY data
              const video = e.target
              if (video && video.paused) {
                // Try to play even with minimal buffer (readyState >= 1)
                if (video.readyState >= 1) {
                  setVideoReady(true)
                  if (heroImage) {
                    setHeroImage('')
                  }
                  // mark displayed index when video has started buffering/playing
                  try {
                    const slides = heroContent?.slides || []
                    const vidIndex = slides.findIndex((s) => s?.backgroundVideo === heroVideo)
                    if (vidIndex >= 0) setDisplayedSlideIndex(vidIndex)
                  } catch (err) {}
                  video.play().catch(() => {})
                }
              }
              // Video continues buffering in background while playing
            }}
            onWaiting={(e) => {
              // Video buffering - wait for more data (normal YouTube behavior)
              // Browser will automatically resume when buffer is ready
            }}
            onPlaying={(e) => {
              // Video is playing - now hide image and mark ready
              setVideoReady(true)
              if (heroImage) setHeroImage('')
              // ensure overlay switches now that video is playing
              try {
                const slides = heroContent?.slides || []
                const vidIndex = slides.findIndex((s) => s?.backgroundVideo === heroVideo)
                if (vidIndex >= 0) setDisplayedSlideIndex(vidIndex)
              } catch (err) {}
            }}
            onEnded={(e) => {
              // Video finished playing - switch to next slide or show image fallback
              const slide = heroContent?.slides?.[currentHeroIndex]
              if (slide?.backgroundImage) {
                // Show image fallback after video ends
                setHeroImage(slide.backgroundImage)
                setHeroVideo('')
              } else {
                // Auto-advance to next slide if available
                const nextIndex = (currentHeroIndex + 1) % slidesCount
                if (nextIndex !== currentHeroIndex && slidesCount > 1) {
                  switchHeroToIndex(nextIndex)
                }
              }
            }}
            onError={(e) => {
              // If video fails, try to show image fallback if available
              const slide = heroContent?.slides?.[currentHeroIndex]
              if (slide?.backgroundImage) {
                setHeroImage(slide.backgroundImage)
                setHeroVideo('')
              }
            }}
          >
            {heroVideo && <source src={heroVideo} type='video/mp4' />}
            Your browser does not support the video tag.
          </video>
          {/* Image display - shows when no video, or as placeholder before video is ready */}
          {heroImage && (
            <img
              className='hero-image active-media'
              src={heroImage}
              alt='Hero image'
              decoding='async'
              loading='eager'
              fetchPriority='high'
              style={{
                opacity: videoReady && heroVideo ? 0 : 1,
                display: videoReady && heroVideo ? 'none' : 'block',
              }}
              onLoad={() => {
                setHeroImageLoaded(true)
                try {
                  const slides = heroContent?.slides || []
                  const imgIndex = slides.findIndex((s) => s?.backgroundImage === heroImage)
                  if (imgIndex >= 0) setDisplayedSlideIndex(imgIndex)
                } catch (err) {}
              }}
              onError={(e) => {
                // Mark as loaded to avoid overlay blocking, then fallback to video if any
                setHeroImageLoaded(true)
                const slide = heroContent?.slides?.[currentHeroIndex]
                if (slide?.backgroundVideo) {
                  setHeroVideo(slide.backgroundVideo)
                  setHeroImage('')
                }
              }}
            />
          )}

         
          {/* REMOVED: Pending media overlay - videos/images now load directly */}
        </div>
        <div className='hero-overlay'>
          <div className='hero-content animate__animated animate__fadeInUp'>
            <h1 className='hero-title'>
              {heroContent.slides[displayedSlideIndex]?.heading ||
                'Welcome to Ampthilldowns'}
            </h1>
            <p className='hero-subtitle'>
              {heroContent.slides[displayedSlideIndex]?.subheading ||
                'An eco-stay in the mountains where nature and comfort come together'}
            </p>
            <div className='hero-button-container'></div>
          </div>
        </div>
      </section>



<section className="ta-section">
  <h2 className="section-head">{testimonialsContent.title || 'Our Guests Rate Us Highly'}</h2>

  <div className="ta-grid fade-in">

    {/* 1 — Official Listing */}
    <a className="ta-card" target="_blank" rel="noopener noreferrer"
       href="https://www.tripadvisor.in/Hotel_Review-g303890-d3561836-Ampthill_Downs-Kodaikanal.html">
      <img src="https://static.tacdn.com/img2/brand_refresh/Tripadvisor_logomark.svg" alt="TripAdvisor"/>
      <p className="ta-label">Official TripAdvisor Listing</p>
    </a>

    {/* 2 — Travellers' Choice */}
    <a className="ta-card" target="_blank" rel="noopener noreferrer"
       href="https://www.tripadvisor.in/Hotel_Review-g303890-d3561836-Ampthill_Downs-Kodaikanal.html#AWARDS">
      <img src="https://cdn-icons-png.flaticon.com/512/2583/2583349.png" alt="Travellers Choice Award"/>
      <p className="ta-label">Traveller’s Choice Award</p>
    </a>

    {/* 3 — Read Reviews */}
    <a className="ta-card" target="_blank" rel="noopener noreferrer"
       href="https://www.tripadvisor.in/Hotel_Review-g303890-d3561836-Ampthill_Downs-Kodaikanal.html#REVIEWS">
      <img src="https://static.tacdn.com/img2/brand_refresh/Tripadvisor_lockup_horizontal_secondary_registered.svg"
        alt="TripAdvisor Excellent Badge" id="CDSWIDEXCLOGO" />
      <p className="ta-label">Read Guest Reviews</p>
    </a>

    {/* 4 — Write a Review */}
    <a className="ta-card" target="_blank" rel="noopener noreferrer"
       href="https://www.tripadvisor.in/UserReviewEdit-g303890-d3561836-Ampthill_Downs-Kodaikanal_Tamil_Nadu.html">
      <img src="https://cdn-icons-png.flaticon.com/512/929/929495.png" alt="5 Star Rating"/>
      <p className="ta-label">Rate Us on TripAdvisor</p>
    </a>

  </div>
</section>








      

      <Container className='accommodation-intro text-center mb-5'>
        <h2 className='intro-subtitle'>{introContent.title}</h2>
        <h2 className='intro-heading'>{introContent.content}</h2>
      </Container>





      {/* Location Section with 2 Columns */}

      <section className='welcome-section'>
        <div className='welcome-row'>
          <div
            className='welcome-image'
            style={{
              backgroundImage: `url(${
                locationContent.backgroundImage || storyImage
              })`,
            }}
          />
          <div className='welcome-text'>
            <h3 className='main-title'>{locationContent.title}</h3>
            <div className='coordinates'>
              <p>
                <strong>Latitude:</strong> {locCoords.latitude}
              </p>
              <p>
                <strong>Longitude:</strong> {locCoords.longitude}
              </p>
              <p>
                <strong>Elevation:</strong> {locCoords.elevation}
              </p>
            </div>

            {/* First Paragraph */}
            <p className='story-paragraph'>{locationParas[0]}</p>

            {/* Second Paragraph */}
            <p>
              {locationParas[1]}
              {!expanded && (
                <span
                  className='read-more-link'
                  onClick={toggleExpanded}
                  type='button'
                >
                  ... Read More
                </span>
              )}
            </p>

            {/* Extra content (hidden until expanded) */}
            {expanded && (
              <>
                {locationParas.slice(2).map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}

                {/* Show Less inline after expanded content */}
                <p>
                  <span
                    className='read-more-link'
                    onClick={toggleExpanded}
                    type='button'
                  >
                    Show Less
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Story Section */}
      {/* How It All Began Section */}
      <section className='story-section'>
        <Container fluid className='px-0'>
          <Row className={`story-row ${storyExpanded ? 'expanded' : ''}`}>
            {/* Left Text Column */}
            <Col xs={12} md={6} className='story-text-col '>
              <h3 className='story-title'>{storyContent.title}</h3>

              {/* First Paragraph */}
              <p className='story-paragraph'>{storyParas[0]}</p>

              {/* Second Paragraph */}
              <p className='story-paragraph'>
                {storyParas[1]}
                {!storyExpanded && (
                  <span
                    className='read-more-link'
                    onClick={toggleStoryExpanded}
                    type='button'
                  >
                    ... Read More
                  </span>
                )}
              </p>

              {/* Hidden Paragraphs */}
              {storyExpanded && (
                <>
                  {storyParas.slice(2).map((paragraph, index) => (
                    <p key={index} className='story-paragraph'>
                      {paragraph}
                    </p>
                  ))}
                  <p>
                    <span
                      className='read-more-link'
                      onClick={toggleStoryExpanded}
                      type='button'
                    >
                      Show Less
                    </span>
                  </p>
                </>
              )}
            </Col>

            {/* Right Image Column */}
            <Col
              xs={12}
              md={6}
              className={`story-image-col p-0 position-relative ${
                storyExpanded ? 'expanded' : ''
              }`}
            >
              <div
                className='story-image'
                style={{
                  backgroundImage: `url(${
                    storyContent.backgroundImage || storyImage
                  })`,
                }}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Accommodation Section */}
      <Container className='benefits-section py-5'>
        <h2 className='section-head'>{accommodationContent.title}</h2>

        <h2 className='accomodationtype-intro'>{accommodationContent.intro}</h2>
        <Row className='g-4 justify-content-center'>
          {(accommodationContent?.types || []).map((benefit, index) => (
            <Col xs={12} md={6} lg={3} key={index}>
              <div
                className='benefit-card'
                style={{ '--bg-img': `url(${benefit.bg || cardBg1})` }} // Set as a CSS custom property
              >
                <div className='benefit-icon'>
                  <img
                    src={benefit.icon || [stay1, stay2, stay3, stay4][index]}
                    alt={benefit.title}
                    loading='lazy'
                    decoding='async'
                  />
                </div>
                <h3 className='benefit-title'>{benefit.title}</h3>
              </div>
            </Col>
          ))}
        </Row>
      </Container>

      <section className='experience-section py-5'>
        <Container>
          {/* 🔹 Title and Intro (your original heading) */}
          <h2 className='section-head'>
            {experiencesContent.title || 'Discover Experiences'}
          </h2>
          <p className='text-center mb-4'>
            {experiencesContent.intro ||
              'Explore the beautiful experiences our resort offers.'}
          </p>

          {/* 🔹 Discover Experiences Cards with Arrows */}
          <div className='experience-scroll-container'>
            {(experiencesContent?.sections?.length || 0) > 0 && (
              <>
                {isMobile
                  ? (
                      // Mobile: render ALL cards for horizontal scroll
                      (experiencesContent?.sections || []).map((item, index) => (
                        <div
                          key={index}
                          className='experience-card'
                          onClick={() => handleExperienceCardClick(item.sectionId, index)}
                          style={{
                            backgroundImage: `url(${item.image || cardBg1})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          <h3 className='experience-title'>{item.title}</h3>
                        </div>
                      ))
                    )
                  : (
                      // Desktop: retain existing 3-card carousel with arrows
                      <>
                        {Array.from({ length: 3 }).map((_, i) => {
                          const sectionsLength = experiencesContent?.sections?.length || 1
                          const index = (discoverIndex + i) % sectionsLength
                          const item = experiencesContent?.sections?.[index]
                          return (
                            <div
                              key={index}
                              className='experience-card'
                              onClick={() => handleExperienceCardClick(item.sectionId, index)}
                              style={{
                                backgroundImage: `url(${
                                  item.image 
                                })`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <h3 className='experience-title'>{item.title}</h3>
                            </div>
                          )
                        })}
                        <button className='arrow left' onClick={handlePrevDiscover}>
                          {'<'}
                        </button>
                        <button className='arrow right' onClick={handleNextDiscover}>
                          {'>'}
                        </button>
                      </>
                    )}
              </>
            )}
          </div>
        </Container>
      </section>

      {/* Sustainable Living Section */}
      <section className='farm-section py-5'>
        <Container>
          <h2 className='section-head'>{sustainableLivingContent.title}</h2>
          <p className='text-center mb-4'>{sustainableLivingContent.intro}</p>

          {/* Card Container with Arrows */}
          <div className='experience-scroll-container'>
            {(sustainableLivingContent?.sections?.length || 0) > 0 && (
              <>
                {isMobile
                  ? (
                      // Mobile: render ALL sustainable cards for horizontal scroll
                      (sustainableLivingContent?.sections || []).map((item, index) => (
                        <div
                          key={index}
                          className='experience-card'
                          style={{
                            backgroundImage: `url(${item.image || cardBg1})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            transition: 'transform 0.5s ease-in-out',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleFarmCardClick(item.sectionId, index)}
                        >
                          <h3 className='experience-title'>{item.title}</h3>
                        </div>
                      ))
                    )
                  : (
                      // Desktop: retain existing 3-card carousel with arrows
                      <>
                        {Array.from({ length: 3 }).map((_, i) => {
                          const sectionsLength = sustainableLivingContent?.sections?.length || 1
                          const index = (sustainableIndex + i) % sectionsLength
                          const item = sustainableLivingContent?.sections?.[index]
                          return (
                            <div
                              key={index}
                              className='experience-card'
                              style={{
                                backgroundImage: `url(${
                                  item.image
                                })`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                transition: 'transform 0.5s ease-in-out',
                                cursor: 'pointer',
                              }}
                              onClick={() => handleFarmCardClick(item.sectionId, index)}
                            >
                              <h3 className='experience-title'>{item.title}</h3>
                            </div>
                          )
                        })}
                        <button className='arrow left' onClick={handlePrevSustainable}>
                          {'<'}
                        </button>
                        <button className='arrow right' onClick={handleNextSustainable}>
                          {'>'}
                        </button>
                      </>
                    )}
              </>
            )}
          </div>
        </Container>
      </section>

      {/* Gallery Section - Start */}

      <StackedGallery
        images={(content?.gallery?.images || []).filter(Boolean)}
        title={content?.gallery?.title}
      />

      {/* Discover Section */}
      <section className='blog-section'>
        <h2 className='section-head'>
          {content?.discover?.title || 'Discover'}
        </h2>
        <div className='blog-container'>
          <div className='blog-row'>
            {(
              content?.discover?.posts || [
                {
                  title: 'Nature & Wildlife',
                  description:
                    'Poondi is home to lush forests, crystal-clear lakes, and diverse wildlife.',
                },
                {
                  title: 'Culture & Heritage',
                  description:
                    'Experience the heritage of a serene village at the edge of the Western Ghats.',
                },
                {
                  title: 'Hills & Serenity',
                  description:
                    'Rolling hills and untouched beauty make Poondi a true hidden gem.',
                },
                {
                  title: 'Eco Living',
                  description:
                    'Discover sustainable living in harmony with nature.',
                },
              ]
            ).map((post, index) => {
              // Use Firebase image if exists, otherwise use hardcoded fallback
              const imageUrl = post.image && post.image.trim() !== '' 
                ? post.image 
                : [blog1, blog2, blog3, blog4][index]
              
              // DEV: Log to verify each post has unique image URL
              if (process.env.NODE_ENV === 'development') {
              }
              return (
                <div key={index} className='blog-card'>
                  <img
                    src={imageUrl}
                    alt={post.title}
                    loading='lazy'
                    decoding='async'
                  />
                  <h3>{post.title}</h3>
                  <p>{post.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section - End */}

      <section className='plan-journey-section'>
        {/* Resort Title and Location Info */}
        <div className='resort-header'>
          <h1 className='section-head'>
            {content?.planJourney?.resortName || 'Ampthill Eco-Resort'}
          </h1>
          <div className='resort-coordinates'>
            <p className='coordinate-text'>
              Latitude:{' '}
              {content?.planJourney?.coordinates?.latitude || '10°11\'33.76"N'}
            </p>
            <p className='coordinate-text'>
              Longitude:{' '}
              {content?.planJourney?.coordinates?.longitude || '77°20\'17.40"E'}
            </p>
            <p className='coordinate-text'>
              Elevation:{' '}
              {content?.planJourney?.coordinates?.elevation || '6,515 ft'}
            </p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className='plan-journey-container'>
          {/* Left Content */}
          <div className='plan-journey-content'>
            <h2 className='section-head'>
              {content?.planJourney?.title ||
                'Plan Your Journey (Let Us Guide You Here)'}
            </h2>
            <p className='journey-text'>
              {content?.planJourney?.description ||
                'Let us guide you to the serene beauty of Poondi, Kodaikanal. Find everything you need to make your trip smooth and memorable — from how to reach us, what to pack, helpful travel tips, and accurate directions.'}
            </p>
            <button
              className='journey-btn'
              onClick={() => {
                navigate('/plan-your-journey')
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
            >
              {content?.planJourney?.ctaText || 'Explore More'}
            </button>
          </div>

          <div className='plan-journey-map'>
            <iframe
              src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5097.741159095042!2d77.3354157760227!3d10.193179369678859!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b077a81850b1f51%3A0xdcb5c6e9897cb2e9!2sAmpthill%20Downs%20-%20The%20Farm%20Stay!5e1!3m2!1sen!2sin!4v1763549083611!5m2!1sen!2sin'
              width='600'
              height='450'
              style={{ border: 0 }}
              allowFullScreen=''
              loading='lazy'
              referrerPolicy='no-referrer-when-downgrade'
            ></iframe>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Start */}
      <section className='testimonials-section py-5'>
        <Container>
          <h2 className='section-head'>{testimonialsContent.title}</h2>

          {/* Review Buttons at the top */}
          <div className='text-center mb-5'>
            <p
              className='mb-4 review-head'
              style={{ fontWeight: 600, color: '#343a40' }}
            >
              Check out our reviews on popular platforms!
            </p>
            <div className='review-buttons-container'>
              <a
                href='https://www.google.com/travel/search?q=Ampthill%20Downs%20The%20Farm%20Stay%20Kodaikanal%20reviews&g2lb=4965990%2C4969803%2C72302247%2C72317059%2C72414906%2C72471280%2C72472051%2C72485658%2C72560029%2C72573224%2C72616120%2C72647020%2C72485658%2C72560029%2C72573224%2C72616120%2C72647020%2C72686036%2C72803964%2C72882230%2C72887412%2C72958624%2C72959983%2C73059275%2C73064764%2C73107089%2C73124299&hl=en-IN&gl=in&cs=1&ssta=1&ts=CAEaRwopEicyJTB4M2IwNzdhODE4NTBiMWY1MToweGRjYjVjNmU5ODk3Y2IyZTkSGhIUCgcI6Q8QChgZEgcI6Q8QChgaGAEyAhAA&qs=CAEyFENnc0k2ZVh5eTVqZDhkcmNBUkFCOAJCCQnpsnyJ6ca13EIJCemyfInpxrXc&ap=ugEHcmV2aWV3cw&ictx=111&ved=0CAAQ5JsGahcKEwjAs8PFzJuQAxUAAAAAHQAAAAAQBA'
                target='_blank'
                rel='noopener noreferrer'
                className='review-btn google'
              >
                <img
                  src={googleLogo}
                  alt='Google Reviews'
                  loading='lazy'
                  decoding='async'
                />
                <span className='btn-text'>View on Google</span>
              </a>
              <a
                href='https://www.tripadvisor.in/Search?q=Ampthill%20Downs%20The%20Farm%20Stay'
                target='_blank'
                rel='noopener noreferrer'
                className='review-btn tripadvisor'
              >
                <img
                  src={tripadvisorLogo}
                  alt='TripAdvisor Reviews'
                  loading='lazy'
                  decoding='async'
                />
                <span className='btn-text'>View on TripAdvisor</span>
              </a>
            </div>



<div className="d-flex justify-content-center ta-widget-wrap">

  <div className="ta-immersive fade-in">

    

    {/* Frosted Content Layer */}
    <div className="ta-content-panel">
      <img className="ta-logo" src={tripadvisorLogo} alt="TripAdvisor" />

      <h3 className="ta-title">What Our Guests Experienced</h3>

      <p className="ta-desc">
        Explore verified stories from travelers — wildlife encounters,
        serene mornings, eco-friendly comfort, and unforgettable stays.
      </p>

      {/* Auto-Rotating Facts */}
      <div className="ta-facts">
        <div className="fact active">🐾 Spotted sambar deer near the cabins</div>
        <div className="fact">🌿 Guests love the sustainable eco-living</div>
        <div className="fact">🔥 Campfire nights & storytelling cherished</div>
        <div className="fact">🦉 Night bird calls amazed visitors</div>
      </div>

      {/* CTA */}
      <a 
        href="https://www.tripadvisor.in/Hotel_Review-g303890-d3561836-Reviews-Ampthill_Downs-Kodaikanal.html#REVIEWS"
        target="_blank"
        rel="noopener noreferrer"
        className="ta-cta glow"
      >
        ⭐ Read & Write Reviews
      </a>
    </div>
  </div>
</div>

          </div>

          <Row className='g-4 justify-content-center'>
            {(testimonialsContent?.reviews || []).map((review, idx) => (
              <Col xs={12} md={6} lg={4} key={idx}>
                <div className='cool-testimonial-card p-4 h-100 d-flex flex-column'>
                  <div className='d-flex justify-content-between align-items-start mb-3'>
                    <div className='d-flex align-items-center'>
                      <div className='avatar-circle me-3'>
                        <span>{review.avatar}</span>
                      </div>
                      <div>
                        <h5 className='author-name mb-0'>{review.name}</h5>
                        <small className='author-loc text-muted'>
                          {review.location}
                        </small>
                      </div>
                    </div>
                    <div className='review-source-logo'>
                      {review.source === 'Google' && (
                        <img
                          src={googleLogo}
                          alt='Google'
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                          }}
                          loading='lazy'
                          decoding='async'
                        />
                      )}
                    </div>
                  </div>
                  <div className='star-rating mb-3'>
                    {Array(review.rating)
                      .fill(0)
                      .map((_, i) => (
                        <span key={i} className='star'>
                          ★
                        </span>
                      ))}
                    {Array(5 - review.rating)
                      .fill(0)
                      .map((_, i) => (
                        <span key={i} className='star empty'>
                          ★
                        </span>
                      ))}
                  </div>
                  <p className='testimonial-quote flex-grow-1'>
                    "{review.quote}"
                  </p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
      {/* Testimonials Section - End */}

      {/* Footer */}
      <Footer />
      <FloatingActionButton />
    </div>
  )
}

export default Home
